/**
 * Dashboard layout helpers — Phase C Wave 2A.1.
 *
 * Layout item shape (wire contract):
 *   { widget_key: string, order: number, size: "compact"|"standard"|"wide" }
 *
 * Legacy user.dashboard_config is a list[str]. This module upgrades the
 * legacy shape into rich layout items on load and downgrades back on
 * save. When DASHBOARD_PERSIST_ENABLED flips true, no client-side
 * migration is needed — the wire contract is already the rich form.
 */

// Per-widget supported sizes. Widgets not listed here support only
// "standard". Keep this in sync with the custom renderer registry when a
// new renderer gains its own resize behaviour.
export const WIDGET_SIZE_OPTIONS = {
  quote_pipeline:          ["compact", "standard"],
  holds:                   ["standard", "wide"],
  current_coach_locations: ["standard", "wide"],
  driver_hos_attention:    ["standard", "wide"],
};

// Default size a widget takes when a user first adds it.
export const DEFAULT_SIZE_FOR = {
  quote_pipeline:          "standard",
  holds:                   "standard",
  current_coach_locations: "standard",
  driver_hos_attention:    "standard",
};

// Fleet Map UX Hardening — per-widget supported HEIGHTS. Widgets not
// listed here render at their intrinsic height and expose NO height
// control in Edit Mode. This is deliberate: dashboard cards should not
// each grow a height knob just because they can.
export const WIDGET_HEIGHT_OPTIONS = {
  current_coach_locations: ["normal", "tall", "extra_tall"],
};

export const DEFAULT_HEIGHT_FOR = {
  current_coach_locations: "normal",
};

export const sizesFor = (widgetKey) =>
  WIDGET_SIZE_OPTIONS[widgetKey] || ["standard"];

export const defaultSizeFor = (widgetKey) =>
  DEFAULT_SIZE_FOR[widgetKey] || "standard";

export const heightsFor = (widgetKey) =>
  WIDGET_HEIGHT_OPTIONS[widgetKey] || [];

export const defaultHeightFor = (widgetKey) =>
  DEFAULT_HEIGHT_FOR[widgetKey] || null;

// Convert a legacy list[str] into the rich layout item list.
export const layoutFromLegacy = (widgetKeys) =>
  (widgetKeys || []).map((widget_key, order) => ({
    widget_key,
    order,
    size: defaultSizeFor(widget_key),
    ...(defaultHeightFor(widget_key)
      ? { height: defaultHeightFor(widget_key) }
      : {}),
  }));

// Fleet Map UX Hardening — normalize a rich layout payload from the
// server. Guarantees `size` on every entry and, for widgets that opt in
// to height controls, guarantees a valid `height`. Legacy list[str]
// callers should use `layoutFromLegacy` above instead.
export const layoutFromServer = (items) =>
  (items || []).map((it, i) => {
    const widget_key = it.widget_key;
    const supportedH = heightsFor(widget_key);
    const rawH = it.height;
    const height = supportedH.includes(rawH)
      ? rawH
      : defaultHeightFor(widget_key);
    return {
      widget_key,
      order: typeof it.order === "number" ? it.order : i,
      size: it.size || defaultSizeFor(widget_key),
      ...(height ? { height } : {}),
    };
  });

// Re-derive `order` from current array position. This is intentionally
// NOT a "sort by order then reindex" — that pattern breaks moveToIndex
// because the caller has already reordered the array in place; sorting
// by the stale order values would undo the move. Any function that
// wants to enforce a specific sort should sort explicitly first, then
// call normalize.
export const normalize = (items) =>
  items.map((it, i) => ({ ...it, order: i }));

// Move a widget up (-1) or down (+1) in the ordered list.
export const moveByDelta = (items, widget_key, delta) => {
  const sorted = normalize(items);
  const idx = sorted.findIndex((it) => it.widget_key === widget_key);
  if (idx < 0) return sorted;
  const newIdx = idx + delta;
  if (newIdx < 0 || newIdx >= sorted.length) return sorted;
  const next = [...sorted];
  const [pulled] = next.splice(idx, 1);
  next.splice(newIdx, 0, pulled);
  return normalize(next);
};

// Move a widget to a specific index (used by HTML5 drag drop).
export const moveToIndex = (items, widget_key, targetIdx) => {
  const sorted = normalize(items);
  const idx = sorted.findIndex((it) => it.widget_key === widget_key);
  if (idx < 0) return sorted;
  const next = [...sorted];
  const [pulled] = next.splice(idx, 1);
  const clamped = Math.max(0, Math.min(targetIdx, next.length));
  next.splice(clamped, 0, pulled);
  return normalize(next);
};

export const setSize = (items, widget_key, size) =>
  normalize(items).map((it) =>
    it.widget_key === widget_key ? { ...it, size } : it
  );

// Fleet Map UX Hardening — height mutation. Same shape/contract as
// `setSize`. Only widgets in WIDGET_HEIGHT_OPTIONS actually surface a
// height selector; callers should still gate the UI.
export const setHeight = (items, widget_key, height) =>
  normalize(items).map((it) =>
    it.widget_key === widget_key ? { ...it, height } : it
  );

export const removeWidget = (items, widget_key) =>
  normalize(items.filter((it) => it.widget_key !== widget_key));

export const addWidget = (items, widget_key) => {
  if (items.some((it) => it.widget_key === widget_key)) return items;
  const next = [
    ...items,
    {
      widget_key,
      order: items.length,
      size: defaultSizeFor(widget_key),
      ...(defaultHeightFor(widget_key)
        ? { height: defaultHeightFor(widget_key) }
        : {}),
    },
  ];
  return normalize(next);
};

// Two layouts are equal iff same widgets, same order, same size, same height.
export const layoutsEqual = (a, b) => {
  const na = normalize(a);
  const nb = normalize(b);
  if (na.length !== nb.length) return false;
  for (let i = 0; i < na.length; i++) {
    if (na[i].widget_key !== nb[i].widget_key) return false;
    if (na[i].size !== nb[i].size) return false;
    if ((na[i].height || null) !== (nb[i].height || null)) return false;
  }
  return true;
};

// Downgrade rich layout → legacy list[str] (in order).
export const layoutToLegacy = (items) =>
  normalize(items).map((it) => it.widget_key);
